import React, { useEffect, useState } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { navigationData } from '../mock';
import { useTheme } from '../context/ThemeContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const sectionIds = navigationData.links
      .map((link) => link.href)
      .filter((href) => href.startsWith('#'))
      .map((href) => href.slice(1));

    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleLinkClick = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleCTAClick = () => {
    const contactElement = document.querySelector('#contact');
    if (contactElement) {
      contactElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="dark-header">
      <img 
        src="https://customer-assets.emergentagent.com/job_ai-safety-hub-1/artifacts/fh6j9upc_Qubyt%20website%20logo%20Transparent.png" 
        alt="QuByt AI"
        style={{
          height: '40px',
          objectFit: 'contain',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
      
      {/* Desktop Navigation */}
      <nav className="dark-nav">
        {navigationData.links.map((link, index) => {
          const sectionId = link.href.startsWith('#') ? link.href.slice(1) : '';
          const isActive = sectionId && sectionId === activeSection;
          return (
            <button
              key={index}
              className={`dark-nav-link${isActive ? ' active' : ''}`}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
              onClick={() => handleLinkClick(link.href)}
            >
              {link.name}
            </button>
          );
        })}

        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>
        
        <button className="btn-primary" onClick={handleCTAClick}>
          {navigationData.cta}
        </button>
      </nav>

      {/* Mobile Controls */}
      <div style={{ display: 'none', alignItems: 'center', gap: '12px' }} className="mobile-controls">
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <button
          className="mobile-menu-btn"
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            padding: '8px'
          }}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div
          className="mobile-nav"
          style={{
            position: 'fixed',
            top: '70px',
            left: 0,
            right: 0,
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-subtle)',
            borderTop: 'none',
            padding: '20px',
            zIndex: 1000,
            maxHeight: 'calc(100vh - 70px)',
            overflowY: 'auto'
          }}
        >
          {navigationData.links.map((link, index) => (
            <button
              key={index}
              style={{
                display: 'block',
                width: '100%',
                padding: '12px 0',
                background: 'none',
                border: 'none',
                color: 'var(--text-primary)',
                textDecoration: 'none',
                fontSize: '18px',
                borderBottom: '1px solid var(--border-subtle)',
                cursor: 'pointer',
                textAlign: 'left'
              }}
              onClick={() => handleLinkClick(link.href)}
            >
              {link.name}
            </button>
          ))}
          
          <button 
            className="btn-primary" 
            style={{ width: '100%', marginTop: '16px' }}
            onClick={handleCTAClick}
          >
            {navigationData.cta}
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
